// Retarget a clip authored on one skeleton onto another rig's rest pose
// (world-delta sampling, rotation-only). Used for shared emotes and for the
// Tripo animation library (src/avatar/tripoLibrary.json), whose clips were all
// made on one library avatar and play on every Tripo-rigged avatar.
import * as THREE from 'three';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { humanoidKey } from './rig';

/** Limb bones whose rest direction is aligned to the source rig before retargeting. */
const LIMB_KEY = /^(left|right)(Shoulder|UpperArm|LowerArm|UpperLeg|LowerLeg|Foot)$/;

// ---- Runtime emote retargeting ----
// Every Meshy avatar gets its own auto-rig with a unique rest pose, so a
// shared emote clip (authored/retargeted on ONE rig) plays back twisted on
// any other. Fix: transfer the clip onto THIS avatar's skeleton at load time
// via world-delta sampling — per frame, take each bone's world-space rotation
// delta (vs rest) on the clip's own skeleton and apply that same world delta
// to the avatar's rest, converting back to bone-local top-down through the
// chain. Rotation-only (no position tracks): hips height is owned by the
// ground-keeper, and animated hip translations fight it (runaway sink).
export function retargetClipToRig(
  srcRoot: THREE.Object3D,          // the emote GLB's own scene (its skeleton)
  clip: THREE.AnimationClip,
  dstRestWorldQ: Map<string, THREE.Quaternion>, // avatar rest world quats (all nodes)
  dstBoneOrder: { name: string; parent: string }[], // avatar bones, parents first
  dstRestWorldPos?: Map<string, THREE.Vector3>, // avatar rest world positions → limb alignment
): THREE.AnimationClip {
  // Source rest pose (before the mixer touches it)
  srcRoot.updateMatrixWorld(true);
  const srcBones = new Map<string, THREE.Object3D>();
  srcRoot.traverse((o) => { if (o.name) srcBones.set(o.name, o); });
  const srcRestWorldQ = new Map<string, THREE.Quaternion>();
  const _wq = new THREE.Quaternion();
  for (const [n, b] of srcBones) { b.getWorldQuaternion(_wq); srcRestWorldQ.set(n, _wq.clone()); }

  // Bones pair up by name, or by body part when the rigs name them
  // differently (a Meshy/Mixamo emote on a Tripo avatar: LeftForeArm ↔
  // L_Forearm, Spine ↔ Waist). Unpaired avatar bones in the chain (Tripo's
  // NeckTwist02, Pelvis) keep their rest offset but still follow their parent.
  const srcByKey = new Map<string, string>();
  for (const n of srcBones.keys()) { const k = humanoidKey(n); if (k && !srcByKey.has(k)) srcByKey.set(k, n); }
  const srcNameFor = (dst: string) => {
    if (srcBones.has(dst)) return dst;
    const k = humanoidKey(dst);
    return k ? srcByKey.get(k) : undefined;
  };
  const pairs = new Map<string, string>();
  for (const b of dstBoneOrder) {
    const sn = srcNameFor(b.name);
    if (sn && srcRestWorldQ.has(sn) && dstRestWorldQ.has(b.name)) pairs.set(b.name, sn);
  }
  const bones = dstBoneOrder.filter((b) => pairs.has(b.name));
  if (bones.length === 0) return clip;
  const restLocal = new Map<string, THREE.Quaternion>();
  for (const { name, parent } of dstBoneOrder) {
    const w = dstRestWorldQ.get(name);
    if (!w || pairs.has(name)) continue;
    const pw = dstRestWorldQ.get(parent) || new THREE.Quaternion();
    restLocal.set(name, pw.clone().invert().multiply(w));
  }

  // Limb alignment. World deltas are only meaningful when both rigs rest in the
  // same pose. Tripo rigs keep whatever pose the picture had: the library avatar
  // rests in a T-pose, a picture in A-pose gives a rig whose arms already hang
  // ~75° lower — the library idle then swings them a further 80° down and across
  // the chest ("twisted"). So first swing each limb of THIS rig onto the source
  // rest direction (shortest arc: bone roll is kept, which also covers the large
  // foot/twist-axis roll differences between Tripo rigs), then apply the delta:
  //   world(t) = D(t) · C · rest,   C = rotation taking our limb onto the source's.
  // Children without a limb key (hands, toes, twist bones, fingers) take their
  // parent's C so they stay attached exactly as in this rig's rest.
  const align = new Map<string, THREE.Quaternion>();
  if (dstRestWorldPos) {
    const kids = new Map<string, string[]>();
    for (const { name, parent } of dstBoneOrder) { if (!kids.has(parent)) kids.set(parent, []); kids.get(parent)!.push(name); }
    const toward = (from: THREE.Vector3, tips: THREE.Vector3[]) => {
      if (!tips.length) return null;
      const v = new THREE.Vector3();
      for (const t of tips) v.add(t);
      v.multiplyScalar(1 / tips.length).sub(from);
      return v.lengthSq() > 1e-10 ? v.normalize() : null;
    };
    const dstDir = (n: string) => {
      const p = dstRestWorldPos.get(n);
      return p ? toward(p, (kids.get(n) || []).map((k) => dstRestWorldPos.get(k)).filter((x): x is THREE.Vector3 => !!x)) : null;
    };
    const srcDir = (n: string) => {
      const b = srcBones.get(n)!;
      return toward(b.getWorldPosition(new THREE.Vector3()), b.children.filter((c) => c.name && srcBones.has(c.name)).map((c) => c.getWorldPosition(new THREE.Vector3())));
    };
    for (const { name, parent } of dstBoneOrder) {
      const sn = pairs.get(name);
      const key = humanoidKey(name);
      if (sn && key && LIMB_KEY.test(key)) {
        const a = dstDir(name), b = srcDir(sn);
        if (a && b) { align.set(name, new THREE.Quaternion().setFromUnitVectors(a, b)); continue; }
      }
      const pc = align.get(parent);
      if (pc) align.set(name, pc.clone());
    }
  }
  const restAligned = new Map<string, THREE.Quaternion>();
  for (const b of bones) {
    const r = dstRestWorldQ.get(b.name)!;
    const c = align.get(b.name);
    restAligned.set(b.name, c ? c.clone().multiply(r) : r);
  }

  const fps = 30;
  const N = Math.max(2, Math.round(clip.duration * fps) + 1);
  const times = new Float32Array(N);
  const out = new Map(bones.map((b) => [b.name, new Float32Array(N * 4)]));

  const mixer = new THREE.AnimationMixer(srcRoot);
  const action = mixer.clipAction(clip);
  action.setLoop(THREE.LoopRepeat, Infinity);
  action.play();

  const D = new THREE.Quaternion(), Wt = new THREE.Quaternion(), ql = new THREE.Quaternion(), inv = new THREE.Quaternion();
  const prev = new Map<string, THREE.Quaternion>();
  for (let f = 0; f < N; f++) {
    const t = Math.min((f / fps), clip.duration - 1e-4);
    times[f] = t;
    mixer.setTime(t);
    srcRoot.updateMatrixWorld(true);
    const worldQ = new Map<string, THREE.Quaternion>();
    for (const { name, parent } of dstBoneOrder) {
      const sn = pairs.get(name);
      if (!sn) {
        const rl = restLocal.get(name);
        const pW0 = worldQ.get(parent);
        if (rl && pW0) worldQ.set(name, pW0.clone().multiply(rl));
        continue;
      }
      srcBones.get(sn)!.getWorldQuaternion(_wq);
      D.copy(_wq).multiply(inv.copy(srcRestWorldQ.get(sn)!).invert());
      Wt.copy(D).multiply(restAligned.get(name)!);
      const pW = worldQ.get(parent) || dstRestWorldQ.get(parent) || new THREE.Quaternion();
      ql.copy(pW).invert().multiply(Wt);
      const pv = prev.get(name);
      if (pv && ql.x * pv.x + ql.y * pv.y + ql.z * pv.z + ql.w * pv.w < 0) ql.set(-ql.x, -ql.y, -ql.z, -ql.w);
      prev.set(name, ql.clone());
      out.get(name)!.set([ql.x, ql.y, ql.z, ql.w], f * 4);
      worldQ.set(name, pW.clone().multiply(ql));
    }
  }
  mixer.stopAllAction();

  const tracks = bones.map(({ name }) => new THREE.QuaternionKeyframeTrack(`${name}.quaternion`, times as any, out.get(name)! as any));
  const rc = new THREE.AnimationClip(clip.name, clip.duration, tracks);
  return rc;
}


/** A rig's rest pose as retargetClipToRig wants it, from a scene still in bind pose. */
export function captureRest(scene: THREE.Object3D) {
  scene.updateMatrixWorld(true);
  const restWorldQ = new Map<string, THREE.Quaternion>();
  const restWorldPos = new Map<string, THREE.Vector3>();
  const boneOrder: { name: string; parent: string }[] = [];
  const _q = new THREE.Quaternion();
  scene.traverse((o: any) => {
    if (!o.name) return;
    o.getWorldQuaternion(_q);
    restWorldQ.set(o.name, _q.clone());
    restWorldPos.set(o.name, o.getWorldPosition(new THREE.Vector3()));
    if (o.isBone) boneOrder.push({ name: o.name, parent: o.parent?.name || '' });
  });
  return { restWorldQ, restWorldPos, boneOrder };
}
export type RigRest = ReturnType<typeof captureRest>;

/** Clips from the shared Tripo library are made on the library avatar's rig, not this one. */
export const isTripoLibraryClip = (url?: string | null) => !!url && url.includes('/tripo-library/');

/**
 * Fit a Tripo library clip onto an avatar: rotation tracks only (hips height is
 * owned by the ground-keeper), retargeted from the library skeleton's rest pose.
 */
export function fitLibraryClip(
  clip: THREE.AnimationClip,
  librarySkeleton: THREE.Object3D,
  rest: RigRest,
): THREE.AnimationClip {
  const c = clip.clone();
  c.tracks = c.tracks.filter((t) => t.name.endsWith('.quaternion'));
  try {
    return retargetClipToRig(clone(librarySkeleton), c, rest.restWorldQ, rest.boneOrder, rest.restWorldPos);
  } catch (err) {
    console.warn('[Avatar] Library clip retarget failed — using raw clip', err);
    return c;
  }
}
